// src/components/BatchCreation.js
import React, { useState, useEffect } from 'react';
import {
  Box, Button, TextField, Typography, MenuItem, Select, InputLabel, FormControl, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import * as XLSX from 'xlsx';
import coursesFile from '../assets/SIDH Courses (Approved).xlsx';
import tcFile from '../assets/TC ID.xlsx';
import { useAuth } from '../context/AuthContext';

const BatchCreation = () => {
  const { isAuthenticated } = useAuth();
  const [form, setForm] = useState({
    batchName: '',
    size: '',
    batchStartDate: '',
    batchEndDate: '',
    courseId: '',
    trainingHoursPerDay: '',
    batchStartTime: '',
    batchEndTime: '',
    assessmentStartDate: '',
    assessmentEndDate: '',
    tcId: '',
  });

  const [courseIdOptions, setCourseIdOptions] = useState([]);
  const [tcIdOptions, setTcIdOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(() => {
    // Try to get last batch creation result from localStorage
    const savedResults = localStorage.getItem('lastBatchCreation');
    return savedResults ? JSON.parse(savedResults) : null;
  });

  useEffect(() => {
    // Load Excel files and parse data
    loadExcelData();
  }, []);

  const loadExcelData = async () => {
    try {
      // Load courses data
      const coursesResponse = await fetch(coursesFile);
      const coursesArrayBuffer = await coursesResponse.arrayBuffer();
      const coursesWorkbook = XLSX.read(coursesArrayBuffer, { type: 'array' });
      const coursesSheet = coursesWorkbook.Sheets[coursesWorkbook.SheetNames[0]];
      const coursesData = XLSX.utils.sheet_to_json(coursesSheet, { 
        raw: false,
        header: ['SI.NO', 'COURSE NAME', 'COURSE ID'] // Exact column headers from Excel
      });
      
      // Debug logs
      console.log('Raw courses data:', coursesData);
      console.log('Sample first row:', coursesData[0]);
      console.log('Available columns:', Object.keys(coursesData[0] || {}));

      // Skip the header row and map the data
      const courses = coursesData.slice(1).map((row) => {
        const courseId = row['COURSE ID'];
        const courseName = row['COURSE NAME'];
        
        // Verify that we have both ID and name, and it's a valid course ID
        if (courseId && courseName && courseId.startsWith('FeeSchCor_')) {
          return {
            value: courseId,
            label: `${courseId} - ${courseName}`
          };
        }
        return null;
      }).filter(course => course !== null);

      console.log('Processed courses:', courses);
      
      setCourseIdOptions(courses);

      // Load TC IDs data
      const tcResponse = await fetch(tcFile);
      const tcArrayBuffer = await tcResponse.arrayBuffer();
      const tcWorkbook = XLSX.read(tcArrayBuffer, { type: 'array' });
      const tcSheet = tcWorkbook.Sheets[tcWorkbook.SheetNames[0]];
      const tcData = XLSX.utils.sheet_to_json(tcSheet);
      
      // Extract TC IDs and names from TC file
      const tcIds = tcData.map(row => {
        let tcId = null;
        let tcName = null;

        // Try to find TC ID and name in the row
        for (const [key, value] of Object.entries(row)) {
          if (value && typeof value === 'string') {
            // Look for TC ID
            if ((key.toUpperCase().includes('TC') && key.toUpperCase().includes('ID')) || 
                key.toUpperCase() === 'TC ID') {
              tcId = value;
            }
            // Look for TC name
            else if (key.toUpperCase().includes('NAME') || 
                    key.toUpperCase().includes('CENTRE') || 
                    key.toUpperCase().includes('CENTER')) {
              tcName = value;
            }
          }
        }

        // If TC ID not found in specific column, try first column
        if (!tcId) {
          tcId = Object.values(row)[0];
        }

        return {
          value: tcId,
          label: tcName ? `${tcId} - ${tcName}` : tcId
        };
      }).filter(tc => tc.value && tc.value !== 'TC ID' && tc.value !== 'TP ID'); // Filter out header row if present
      
      setTcIdOptions(tcIds);
    } catch (error) {
      console.error('Error loading Excel files:', error);
      // Fallback to static data
      setCourseIdOptions([
        { value: 'FeeSchCor_69942', label: 'FeeSchCor_69942' },
        { value: 'FeeSchCor_69943', label: 'FeeSchCor_69943' }
      ]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResults(null);
    
    // Format the data to match the required JSON structure
    const formattedData = {
      batchName: form.batchName,
      size: Number(form.size),
      batchStartDate: form.batchStartDate ? new Date(form.batchStartDate).toISOString() : '',
      batchEndDate: form.batchEndDate ? new Date(form.batchEndDate).toISOString() : '',
      courseId: form.courseId,
      trainingHoursPerDay: Number(form.trainingHoursPerDay),
      batchStartTime: form.batchStartTime ? new Date(form.batchStartTime).toISOString() : '',
      batchEndTime: form.batchEndTime ? new Date(form.batchEndTime).toISOString() : '',
      assessmentStartDate: form.assessmentStartDate ? new Date(form.assessmentStartDate).toISOString() : '',
      assessmentEndDate: form.assessmentEndDate ? new Date(form.assessmentEndDate).toISOString() : '',
      tcId: form.tcId,
    };
    
    try {
      // Get the auth token from localStorage
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Please login first to create a batch');
        setLoading(false);
        return;
      }
      
      console.log('Sending batch creation request to backend...');
      
      // Send data to backend API (backend will use stored session cookies from login)
      const response = await fetch('https://sims.ethnotech.in/api/batch/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formattedData)
      });
      
      const result = await response.json();
      
      if (response.ok) {
        // Format success result for display
        const formattedResult = {
          success: true,
          message: 'Batch created successfully',
          batchDetails: {
            batchName: form.batchName,
            batchId: result.batchId || result.id || 'Generated',
            courseId: form.courseId,
            size: form.size,
            type: form.type,
            status: 'Created',
            statusCode: response.status,
            response: result
          }
        };
        
        // Save results both to state and localStorage
        setResults(formattedResult);
        localStorage.setItem('lastBatchCreation', JSON.stringify(formattedResult));
        
        // Reset form after successful creation
        setForm({
          batchName: '',
          size: '',
          batchStartDate: '',
          batchEndDate: '',
          courseId: '',
          trainingHoursPerDay: '',
          batchStartTime: '',
          batchEndTime: '',
          assessmentStartDate: '',
          assessmentEndDate: '',
          tcId: '',
        });
      } else {
        const errorMessage = result.message || result.detail || 'Unknown error occurred';
        
        // Format error result for display
        const formattedResult = {
          success: false,
          message: `Batch creation failed: ${errorMessage}`,
          batchDetails: {
            batchName: form.batchName,
            batchId: 'N/A',
            courseId: form.courseId,
            size: form.size,
            type: form.type,
            status: 'Failed',
            statusCode: response.status,
            response: result
          }
        };
        
        setResults(formattedResult);
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = `Network error: ${error.message}`;
      setError(errorMessage);
      
      // Format network error result for display
      const formattedResult = {
        success: false,
        message: errorMessage,
        batchDetails: {
          batchName: form.batchName,
          batchId: 'N/A',
          courseId: form.courseId,
          size: form.size,
          type: form.type,
          status: 'Error',
          statusCode: 'Network Error',
          response: { error: error.message }
        }
      };
      
      setResults(formattedResult);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!results) return;

    try {
      const csvHeaders = ['Batch Name', 'Batch ID', 'Course ID', 'Size', 'Type', 'Status', 'Status Code', 'Response Message'];
      
      const csvRow = [
        results.batchDetails.batchName,
        results.batchDetails.batchId,
        results.batchDetails.courseId,
        results.batchDetails.size,
        results.batchDetails.type,
        results.batchDetails.status,
        results.batchDetails.statusCode,
        JSON.stringify(results.batchDetails.response)
      ].map(field => `"${String(field || '').replace(/"/g, '""')}"`);

      const csvContent = [
        csvHeaders.join(','),
        csvRow.join(',')
      ].join('\r\n');

      // Create and trigger download
      const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `batch_creation_result_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to generate download. Please try again.');
    }
  };

  const getStatusIcon = (success) => {
    return success ? '✅' : '❌';
  };

  const getStatusColor = (success) => {
    return success ? '#4caf50' : '#f44336';
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Batch Creation
      </Typography>
      <Paper sx={{ p: 3 }}>
        {error && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
            <Typography color="error">⚠️ {error}</Typography>
          </Box>
        )}

        <form onSubmit={handleSubmit}>
          <TextField fullWidth margin="normal" label="Batch Name" name="batchName" value={form.batchName} onChange={handleChange} required />
          <TextField fullWidth margin="normal" label="Batch Size" name="size" type="number" value={form.size} onChange={handleChange} required />
          <TextField fullWidth margin="normal" label="Batch Start Date" name="batchStartDate" type="date" value={form.batchStartDate} onChange={handleChange} InputLabelProps={{ shrink: true }} required />
          <TextField fullWidth margin="normal" label="Batch End Date" name="batchEndDate" type="date" value={form.batchEndDate} onChange={handleChange} InputLabelProps={{ shrink: true }} required />
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="courseid-label">Course ID</InputLabel>
            <Select labelId="courseid-label" name="courseId" value={form.courseId} label="Course ID" onChange={handleChange} required>
              {courseIdOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField fullWidth margin="normal" label="Training Hours Per Day" name="trainingHoursPerDay" type="number" value={form.trainingHoursPerDay} onChange={handleChange} required />
          <TextField fullWidth margin="normal" label="Batch Start Time" name="batchStartTime" type="datetime-local" value={form.batchStartTime} onChange={handleChange} InputLabelProps={{ shrink: true }} required />
          <TextField fullWidth margin="normal" label="Batch End Time" name="batchEndTime" type="datetime-local" value={form.batchEndTime} onChange={handleChange} InputLabelProps={{ shrink: true }} required />
          
          
          <TextField fullWidth margin="normal" label="Assessment Start Date" name="assessmentStartDate" type="date" value={form.assessmentStartDate} onChange={handleChange} InputLabelProps={{ shrink: true }} required />
          <TextField fullWidth margin="normal" label="Assessment End Date" name="assessmentEndDate" type="date" value={form.assessmentEndDate} onChange={handleChange} InputLabelProps={{ shrink: true }} required />
          
          
          
          
          
          
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="tcId-label">TC ID</InputLabel>
            <Select labelId="tcId-label" name="tcId" value={form.tcId} label="TC ID" onChange={handleChange} required>
              {tcIdOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            fullWidth 
            sx={{ mt: 2 }}
            disabled={loading}
          >
            {loading ? 'Creating Batch...' : 'Create Batch'}
          </Button>
        </form>
      </Paper>

      {results && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">Batch Creation Result</Typography>
            <Button onClick={handleDownload} variant="outlined" startIcon="📥">
              Download CSV
            </Button>
          </Box>

          <Box sx={{ mb: 2, p: 2, bgcolor: results.success ? '#e8f5e8' : '#ffebee', borderRadius: 1 }}>
            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ color: getStatusColor(results.success) }}>
                {getStatusIcon(results.success)}
              </span>
              {results.message}
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Field</TableCell>
                  <TableCell>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><strong>Batch Name</strong></TableCell>
                  <TableCell>{results.batchDetails.batchName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Batch ID</strong></TableCell>
                  <TableCell>{results.batchDetails.batchId}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Course ID</strong></TableCell>
                  <TableCell>{results.batchDetails.courseId}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Size</strong></TableCell>
                  <TableCell>{results.batchDetails.size}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell>{results.batchDetails.type}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell>
                    <span style={{ color: getStatusColor(results.success) }}>
                      {results.batchDetails.status}
                    </span>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Status Code</strong></TableCell>
                  <TableCell>{results.batchDetails.statusCode}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default BatchCreation;